import { isDefined } from "@clipboard-health/util-ts";

import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import { type PlacementData } from "../types/placement";

export const PLACEMENT_VERIFIED_TYPES = {
  INSTANT_INTERVIEW: "INSTANT_INTERVIEW",
  REQUEST_INTERVIEW: "REQUEST_INTERVIEW",
  APPLY: "APPLY",
  APPLY_ON_WEB: "APPLY_ON_WEB",
} as const;
export type PlacementVerifiedType =
  (typeof PLACEMENT_VERIFIED_TYPES)[keyof typeof PLACEMENT_VERIFIED_TYPES];

function getVerifiedTypeFromData(
  placement: Pick<
    PlacementData,
    "interviewSettings" | "webApplicationLink" | "hiringEvents" | "allowRequestInterview"
  >
): PlacementVerifiedType {
  const { hiringEvents, interviewSettings, webApplicationLink, allowRequestInterview } = placement;
  const hasApplicationLink = isDefined(webApplicationLink) && webApplicationLink.trim() !== "";

  if (isDefined(interviewSettings) && interviewSettings.scheduleInterviews) {
    return PLACEMENT_VERIFIED_TYPES.INSTANT_INTERVIEW;
  }

  if (isDefined(hiringEvents)) {
    const hasOpenHouse = hiringEvents.some((event) => event.type === "OpenHouse");
    if (hasOpenHouse) {
      return PLACEMENT_VERIFIED_TYPES.INSTANT_INTERVIEW;
    }
  }

  if (allowRequestInterview) {
    return PLACEMENT_VERIFIED_TYPES.REQUEST_INTERVIEW;
  }

  if (hasApplicationLink) {
    return PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB;
  }

  return PLACEMENT_VERIFIED_TYPES.APPLY;
}

function getVerifiedTypeFromDetails(
  placement: Pick<
    PlacementWithDetails,
    "interviewSettings" | "webApplicationLink" | "hiringEvents" | "allowRequestInterview"
  >
): PlacementVerifiedType {
  const { hiringEvents, interviewSettings, webApplicationLink, allowRequestInterview } = placement;
  const hasApplicationLink = isDefined(webApplicationLink) && webApplicationLink.trim() !== "";

  if (isDefined(interviewSettings) && interviewSettings.attributes.scheduleInterviews) {
    return PLACEMENT_VERIFIED_TYPES.INSTANT_INTERVIEW;
  }

  if (isDefined(hiringEvents)) {
    const hasOpenHouse = hiringEvents.some((event) => event.attributes.type === "OpenHouse");
    if (hasOpenHouse) {
      return PLACEMENT_VERIFIED_TYPES.INSTANT_INTERVIEW;
    }
  }

  if (allowRequestInterview) {
    return PLACEMENT_VERIFIED_TYPES.REQUEST_INTERVIEW;
  }

  if (hasApplicationLink) {
    return PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB;
  }

  return PLACEMENT_VERIFIED_TYPES.APPLY;
}

function isPlacementWithDetails(
  placement:
    | Pick<
        PlacementData,
        "interviewSettings" | "webApplicationLink" | "hiringEvents" | "allowRequestInterview"
      >
    | Pick<
        PlacementWithDetails,
        "interviewSettings" | "webApplicationLink" | "hiringEvents" | "allowRequestInterview"
      >
): placement is PlacementWithDetails {
  return (
    "interviewSettings" in placement &&
    isDefined(placement.interviewSettings) &&
    "attributes" in placement.interviewSettings
  );
}

export function getVerifiedType(
  placement:
    | Pick<
        PlacementData,
        "interviewSettings" | "webApplicationLink" | "hiringEvents" | "allowRequestInterview"
      >
    | Pick<
        PlacementWithDetails,
        "interviewSettings" | "webApplicationLink" | "hiringEvents" | "allowRequestInterview"
      >
) {
  if (isPlacementWithDetails(placement)) {
    return getVerifiedTypeFromDetails(placement);
  }

  return getVerifiedTypeFromData(
    placement as Pick<
      PlacementData,
      "interviewSettings" | "webApplicationLink" | "hiringEvents" | "allowRequestInterview"
    >
  );
}
